import React, { useState, useEffect, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    Chip,
    CircularProgress,
    Alert,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from "@mui/material";
import Axios from "axios";
import { getAuthHeader } from "../../utils/authToken";
import { Field } from "./prepUi";

// Comma-separated seeds — ETT matches titles by substring, so the half-length SAT
// series ("Half SAT n") needs its own seed alongside the full-length one.
const CATALOG_SEED = { SAT: "Digital SAT,Half SAT", ACT: "ACT", AP: "AP" };
// JS getUTCDay(): 0=Sun..6=Sat. Presented Mon-first.
const WEEKDAYS = [
    { label: "Mon", day: 1 }, { label: "Tue", day: 2 }, { label: "Wed", day: 3 },
    { label: "Thu", day: 4 }, { label: "Fri", day: 5 }, { label: "Sat", day: 6 }, { label: "Sun", day: 0 },
];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Date math done in UTC so calendar dates never shift under DST; the strings we
// emit ("YYYY-MM-DDTHH:mm") are NY wall-clock and the server converts to UTC.
const pad = (n) => String(n).padStart(2, "0");
const parseYmd = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(Date.UTC(y, m - 1, d)); };
const toYmd = (dt) => `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
const addDays = (dt, n) => new Date(dt.getTime() + n * 86400000);
const fmtSlot = (s) => {
    const [date, time] = s.split("T");
    const d = parseYmd(date);
    return `${DOW[d.getUTCDay()]} ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()} · ${time}`;
};

const chipLabel = (groupKey, e) =>
    groupKey === "A_SERIES" ? `A${e.num}` : (e.num != null ? String(e.num) : e.title);

// Bulk "schedule builder": pick weekday(s) + a fixed release time, a due offset,
// a start date and a week count → generates dated slots; pick that many exams from
// the catalog (in order) and create them all at once. Manual add stays separate.
const ScheduleExamsDialog = ({ open, onClose, programUuid, programType, existingExams = [], onCreated }) => {
    const programsBase = `${process.env.REACT_APP_URL}/api/v1/prep-programs`;
    const auth = { headers: { Authorization: getAuthHeader() } };

    const [catalog, setCatalog] = useState([]);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [weekdays, setWeekdays] = useState([1]); // Mon
    const [releaseTime, setReleaseTime] = useState("18:00");
    const [dueOffsetDays, setDueOffsetDays] = useState(3);
    const [dueTime, setDueTime] = useState("23:59");
    const [startDate, setStartDate] = useState("");
    const [weeks, setWeeks] = useState(8);
    const [perDate, setPerDate] = useState(1); // exams sharing each release date
    const [selected, setSelected] = useState([]); // ordered [{ id, title }]
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const loadCatalog = useCallback(async () => {
        setCatalogLoading(true);
        try {
            const res = await Axios.get(`${programsBase}/exam-catalog`, {
                ...auth,
                params: { q: CATALOG_SEED[programType] || "" },
            });
            setCatalog(res.data.groups || []);
        } catch (e) {
            setCatalog([]);
        } finally {
            setCatalogLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programType]);

    useEffect(() => {
        if (!open) return;
        setWeekdays([1]); setReleaseTime("18:00"); setDueOffsetDays(3); setDueTime("23:59");
        setStartDate(""); setWeeks(8); setPerDate(1); setSelected([]); setError("");
        loadCatalog();
    }, [open, loadCatalog]);

    // How many exams share each release date. Floor to a positive integer so totalSlots stays
    // an integer (a fractional value would make `selected.length === totalSlots` unsatisfiable).
    const perDateCount = Math.max(1, Math.floor(Number(perDate) || 1));
    const totalSlots = (Number(weeks) || 0) * weekdays.length * perDateCount;

    // Chronological release/due slots from the weekly pattern.
    const slots = (() => {
        if (!startDate || !Number(weeks) || weekdays.length === 0) return [];
        const start = parseYmd(startDate);
        const out = [];
        weekdays.forEach((wd) => {
            let first = start;
            while (first.getUTCDay() !== wd) first = addDays(first, 1);
            for (let w = 0; w < Number(weeks); w++) {
                const rel = addDays(first, w * 7);
                const due = addDays(rel, Number(dueOffsetDays) || 0);
                out.push({ release: `${toYmd(rel)}T${releaseTime}`, due: `${toYmd(due)}T${dueTime}` });
            }
        });
        out.sort((a, b) => a.release.localeCompare(b.release));
        // Repeat each date perDateCount times, kept contiguous, so the exams picked for one date
        // all get the same release/due — letting several exams share a release date.
        if (perDateCount <= 1) return out;
        return out.flatMap((s) => Array.from({ length: perDateCount }, () => ({ ...s })));
    })();

    const addedIds = new Set(existingExams.map((e) => e.ett_exam_id));
    const maxOrder = existingExams.reduce((m, e) => Math.max(m, e.order || 0), 0);

    const toggleExam = (e) => {
        setSelected((prev) => {
            const idx = prev.findIndex((s) => s.id === e.id);
            if (idx >= 0) return prev.filter((s) => s.id !== e.id);
            if (prev.length >= totalSlots) return prev; // cap at slot count
            return [...prev, { id: e.id, title: e.title }];
        });
    };

    const toggleWeekday = (day) =>
        setWeekdays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));

    const rows = slots.map((slot, i) => ({ slot, exam: selected[i], order: maxOrder + 1 + i }));
    const ready = totalSlots > 0 && selected.length === totalSlots && slots.length === totalSlots;

    const create = async () => {
        setBusy(true);
        setError("");
        try {
            const payload = {
                exams: rows.map((r) => ({
                    ett_exam_id: r.exam.id,
                    exam_title: r.exam.title,
                    release_date: r.slot.release,
                    due_date: r.slot.due,
                    order: r.order,
                })),
            };
            await Axios.post(`${programsBase}/${programUuid}/exams/bulk`, payload, auth);
            if (onCreated) onCreated();
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || "Failed to create exams");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Add exams on a schedule</DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                    All times are Eastern Time (America/New_York).
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                    <Field label="Release on">
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                            {WEEKDAYS.map((wd) => (
                                <Chip
                                    key={wd.day}
                                    label={wd.label}
                                    size="small"
                                    color={weekdays.includes(wd.day) ? "primary" : "default"}
                                    variant={weekdays.includes(wd.day) ? "filled" : "outlined"}
                                    onClick={() => toggleWeekday(wd.day)}
                                />
                            ))}
                        </Box>
                    </Field>

                    <Box display="flex" gap={2} flexWrap="wrap">
                        <Field label="Release time (ET)">
                            <TextField type="time" size="small" value={releaseTime}
                                onChange={(e) => setReleaseTime(e.target.value)} />
                        </Field>
                        <Field label="Due (days after)">
                            <TextField type="number" size="small" sx={{ width: 120 }} value={dueOffsetDays}
                                onChange={(e) => setDueOffsetDays(e.target.value)} />
                        </Field>
                        <Field label="Due time (ET)">
                            <TextField type="time" size="small" value={dueTime}
                                onChange={(e) => setDueTime(e.target.value)} />
                        </Field>
                    </Box>

                    <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
                        <Field label="Start date">
                            <TextField type="date" size="small" value={startDate}
                                onChange={(e) => setStartDate(e.target.value)} />
                        </Field>
                        <Field label="Weeks">
                            <TextField type="number" size="small" sx={{ width: 100 }} value={weeks}
                                onChange={(e) => setWeeks(e.target.value)} />
                        </Field>
                        <Field label="Exams per date">
                            <TextField type="number" size="small" sx={{ width: 120 }} inputProps={{ min: 1, step: 1 }} value={perDate}
                                onChange={(e) => setPerDate(e.target.value)} />
                        </Field>
                        <Typography variant="body2" color="text.secondary" sx={{ pb: 1 }}>
                            = {totalSlots} exam{totalSlots === 1 ? "" : "s"}{perDateCount > 1 ? ` (${perDateCount} per date)` : ""}
                        </Typography>
                    </Box>

                    <Field label={`Pick exams in order — ${selected.length}/${totalSlots} selected`}>
                        {catalogLoading ? (
                            <Box display="flex" justifyContent="center" py={2}><CircularProgress size={22} /></Box>
                        ) : catalog.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">No catalog exams found.</Typography>
                        ) : (
                            <Box sx={{ maxHeight: 180, overflow: "auto", pr: 0.5 }}>
                                {catalog.map((g) => (
                                    <Box key={g.key} sx={{ mb: 1 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                            {g.label}
                                        </Typography>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                            {g.exams.map((e) => {
                                                const added = addedIds.has(e.id);
                                                const pick = selected.findIndex((s) => s.id === e.id);
                                                const isSel = pick >= 0;
                                                return (
                                                    <Chip
                                                        key={e.id}
                                                        size="small"
                                                        label={isSel ? `${pick + 1}. ${chipLabel(g.key, e)}` : chipLabel(g.key, e)}
                                                        color={isSel ? "primary" : added ? "default" : e.adaptive ? "info" : "default"}
                                                        variant={isSel || added ? "filled" : "outlined"}
                                                        disabled={added || (!isSel && selected.length >= totalSlots)}
                                                        onClick={() => toggleExam(e)}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Field>

                    {rows.length > 0 && (
                        <Field label="Preview">
                            <Table size="small" sx={{ "& td, & th": { borderColor: "divider" } }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        <TableCell>Exam</TableCell>
                                        <TableCell>Release (ET)</TableCell>
                                        <TableCell>Due (ET)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((r, i) => (
                                        <TableRow key={i}>
                                            <TableCell>{r.order}</TableCell>
                                            <TableCell>
                                                {r.exam ? r.exam.title : <Typography variant="body2" color="text.disabled">pick an exam</Typography>}
                                            </TableCell>
                                            <TableCell>{fmtSlot(r.slot.release)}</TableCell>
                                            <TableCell>{fmtSlot(r.slot.due)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Field>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={create} disabled={!ready || busy}>
                    {busy ? "Creating…" : `Create ${totalSlots} exam${totalSlots === 1 ? "" : "s"}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ScheduleExamsDialog;
